import { useEffect } from 'react';
import { X, FileDown, FileQuestion } from 'lucide-react';

interface DocumentPreviewModalProps {
  open: boolean;
  fileName: string;
  contentType: string;
  blobUrl: string | null;
  loading?: boolean;
  onClose: () => void;
  onDownload: () => void;
}

function isImage(ct: string) {
  return ct.startsWith('image/');
}

function isPdf(ct: string) {
  return ct === 'application/pdf';
}

function isHtml(ct: string) {
  return ct === 'text/html' || ct === 'application/xhtml+xml';
}

function canPreview(ct: string) {
  return isImage(ct) || isPdf(ct) || isHtml(ct);
}

export default function DocumentPreviewModal({
  open,
  fileName,
  contentType,
  blobUrl,
  loading,
  onClose,
  onDownload,
}: DocumentPreviewModalProps) {
  // Close on Escape key
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/60" onClick={onClose} />

      {/* Modal container */}
      <div className="relative z-10 flex flex-col h-full m-4 lg:m-8 rounded-xl overflow-hidden bg-white dark:bg-gray-800 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{fileName}</h3>
            <p className="text-xs text-gray-400 dark:text-gray-500">{contentType}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={onDownload} className="btn-secondary gap-1 text-xs py-1.5 px-3">
              <FileDown className="h-3.5 w-3.5" />
              Download
            </button>
            <button onClick={onClose} className="btn-icon">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto flex items-center justify-center bg-gray-100 dark:bg-gray-900">
          {loading ? (
            <div className="flex flex-col items-center gap-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading preview...</p>
            </div>
          ) : !blobUrl ? (
            <div className="flex flex-col items-center gap-3 p-8">
              <FileQuestion className="h-12 w-12 text-gray-300 dark:text-gray-600" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Failed to load document.</p>
            </div>
          ) : !canPreview(contentType) ? (
            <div className="flex flex-col items-center gap-3 p-8">
              <FileQuestion className="h-12 w-12 text-gray-300 dark:text-gray-600" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Preview not available for {contentType || 'this file type'}.
              </p>
              <button onClick={onDownload} className="btn-primary gap-1 text-sm">
                <FileDown className="h-4 w-4" />
                Download instead
              </button>
            </div>
          ) : isPdf(contentType) ? (
            <iframe
              src={blobUrl}
              className="w-full h-full border-0"
              title={`Preview of ${fileName}`}
            />
          ) : isImage(contentType) ? (
            <img
              src={blobUrl}
              alt={fileName}
              className="max-w-full max-h-full object-contain p-4"
            />
          ) : isHtml(contentType) ? (
            <iframe
              src={blobUrl}
              className="w-full h-full border-0 bg-white"
              title={`Preview of ${fileName}`}
              sandbox="allow-same-origin"
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}
